import { useState } from "react";
import { useTranslation } from "react-i18next";

import UnitIcon from "../../../components/base/UnitIcon";
import { Badge } from "../../../components/ui";
import type { MonitoringUnitRow } from "../hooks/useMonitoringCenter";
import { CASE_URGENCY_TONES, WATCH_STATUS_MAP_COLORS } from "../theme";
import { formatAgo } from "../services/formatWatchTime";

export interface MonitoringRosterProps {
  rows: MonitoringUnitRow[];
  selectedUnitId: string | null;
  onSelect: (unitId: string | null) => void;
  /**
   * Abre el rastro de ESA unidad.
   *
   * El rastro vivía tras un desplegable en el mapa: había que abrir el panel,
   * desplegar la lista y buscar la unidad en un `<select>` — tres gestos para
   * algo que se decide mirando la lista que ya está a la izquierda. Aquí es uno.
   */
  onVerRastro?: (deviceId: string, horas: number) => void;
  /** Cuál está dibujada ahora, para marcar su botón. */
  rastroDeviceId?: string | null;
  /** Se está leyendo el día de esa unidad. El icono gira mientras tanto. */
  rastroCargando?: boolean;
}

/** Las ventanas que se ofrecen, en horas hacia atrás desde ahora. */
const VENTANAS = [3, 6, 12, 24] as const;

export default function MonitoringRoster({
  rows,
  selectedUnitId,
  onSelect,
  onVerRastro,
  rastroDeviceId = null,
  rastroCargando = false,
}: MonitoringRosterProps) {
  const { t } = useTranslation("common");

  /*
    Qué fila tiene desplegado el selector de ventana.
    Se despliega al tocar el icono y se PLIEGA al elegir la hora: el gesto
    termina donde empieza el resultado, y dejarlo abierto obliga a un tercer
    clic para quitarlo de en medio.
  */
  const [abierta, setAbierta] = useState<string | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-card">
      <div className="flex shrink-0 items-center justify-between border-b border-line px-3 py-2">
        <span className="font-mono text-[9.5px] uppercase tracking-widest text-content-muted">
          {t("monitoreoIa.monitoringCenter.roster")}
        </span>
        <span className="font-mono text-[9.5px] text-content-faint">{rows.length}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="px-3 py-8 text-center text-[11px] text-content-faint">
            {t("monitoreoIa.monitoringCenter.rosterEmpty")}
          </p>
        ) : (
          rows.map((row) => {
            const selected = row.unitId === selectedUnitId;

            return (
              <div
                key={row.unitId}
                className={`relative border-b border-line-soft transition-colors ${
                  selected ? "bg-primary-500/10" : "hover:bg-subtle"
                }`}
              >
                {/* El rastro va como botón aparte y NO dentro del de la fila:
                    un botón dentro de otro es HTML inválido y el navegador lo
                    resuelve como le parece —normalmente disparando los dos—. */}
                {onVerRastro && row.deviceId ? (
                  <button
                    type="button"
                    onClick={() =>
                      setAbierta((actual) => (actual === row.unitId ? null : row.unitId))
                    }
                    aria-expanded={abierta === row.unitId}
                    title={t("monitoreoIa.rastro.verDeEsta", {
                      defaultValue: "Ver el rastro de esta unidad",
                    })}
                    className={`absolute right-2 top-2 z-10 rounded-md p-1 max-sm:flex max-sm:h-10 max-sm:w-10 max-sm:items-center max-sm:justify-center transition-colors ${
                      rastroDeviceId === row.deviceId
                        ? "bg-info/20 text-info"
                        : "text-content-faint hover:bg-subtle hover:text-content"
                    }`}
                  >
                    {/* El loader va en el MISMO icono y no al lado: leer el día
                        de una unidad tarda, y sin señal en el sitio donde se
                        tocó parece que el clic no hizo nada. */}
                    <i
                      className={
                        rastroCargando && rastroDeviceId === row.deviceId
                          ? "ri-loader-4-line animate-spin text-[13px] motion-reduce:animate-none"
                          : "ri-route-line text-[13px]"
                      }
                      aria-hidden="true"
                    />
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => onSelect(selected ? null : row.unitId)}
                  className="w-full px-3 py-2 pr-9 text-left max-sm:pr-14"
                >
                {/*
                  La misma fila que en mapa de unidades: icono del vehículo y
                  nombre. Antes era un puntito de color y la placa en monoespacio
                  —correcto para un tablero, pero esta lista es la de siempre y
                  obligaba a reaprenderla. El estado sigue estando, ahora como
                  borde del icono en vez de un punto suelto.

                  DÓNDE ESTÁ Y CUÁNDO REPORTÓ vuelven a esta fila. Estuvieron
                  fuera con el argumento de que un número que cambia cada
                  minuto sólo produce movimiento en una lista que se recorre
                  con la vista, y el argumento no era malo — pero la pregunta
                  «¿dónde anda?» se hace sobre la flota, recorriendo, no
                  abriendo una por una. En la tarjeta de la unidad esas dos
                  líneas quedaban arriba del caso y lo empujaban fuera de la
                  vista, que era peor.
                */}
                <div className="flex items-center gap-2.5">
                  <span
                    className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md bg-subtle"
                    style={{
                      boxShadow: `inset 0 0 0 1.5px ${WATCH_STATUS_MAP_COLORS[row.status]}`,
                    }}
                  >
                    <UnitIcon
                      iconId={row.unit?.icon_id}
                      icon={row.unit?.icon}
                      type={row.unit?.type}
                      model={row.unit?.model}
                      brand={row.unit?.brand}
                      photoFallback
                      className="h-full w-full !object-contain"
                      fallback={
                        <i className="ri-truck-line flex h-full w-full items-center justify-center text-base text-content-faint" />
                      }
                    />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-[12.5px] font-semibold text-content">
                        {row.name}
                      </span>
                      {row.isCalling ? (
                        <i className="ri-phone-fill shrink-0 text-[11px] text-primary-500" />
                      ) : null}
                    </span>
                    <span className="block truncate text-[10.5px] text-content-muted">
                      {row.plate ?? row.driver ?? t("monitoreoIa.units.noDriver")}
                    </span>

                    {/* La dirección en su propia línea y truncada: una calle
                        larga partida en dos renglones descuadra toda la lista.
                        Y cuando no se sabe dónde está, se dice — no se deja el
                        hueco, que se lee como si la línea no hubiera cargado. */}
                    <span className="block truncate text-[10px] text-content-faint">
                      {row.address ?? t("monitoreoIa.units.noLocation")}
                    </span>

                    <span className="block truncate font-mono text-[9.5px] text-content-faint">
                      {row.speedKph == null
                        ? t("time.noData")
                        : t("monitoreoIa.units.speedValue", { speed: row.speedKph })}
                      {" · "}
                      {formatAgo(row.updatedAt, t)}
                    </span>
                  </span>
                </div>

                {row.openCase ? (
                  <div className="mt-1 flex items-center gap-1.5 pl-[46px]">
                    <Badge tone={CASE_URGENCY_TONES[row.openCase.urgency]} size="sm">
                      {t(`monitoreoIa.caseEvents.urgency.${row.openCase.urgency}`)}
                    </Badge>
                    <span className="truncate text-[10px] text-content-secondary">
                      {row.openCaseLabel}
                    </span>
                  </div>
                  ) : null}
                </button>

                {/* El deslizante de ventanas. Vive DENTRO de la fila y no en el
                    mapa: la unidad y el tramo se deciden a la vez, y tenerlos
                    en dos esquinas de la pantalla partía una sola decisión. */}
                {abierta === row.unitId && row.deviceId ? (
                  <div className="flex items-center gap-1 border-t border-line-soft bg-subtle/60 px-3 py-1.5">
                    {VENTANAS.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => {
                          onVerRastro?.(row.deviceId as string, h);
                          setAbierta(null);
                        }}
                        className="flex-1 rounded px-1 py-0.5 font-mono text-[9.5px] tabular-nums text-content-faint transition-colors hover:bg-info/20 hover:text-info"
                      >
                        {h}h
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
